/**
 * KAN-976: чистые функции кошелька: формат рублей, разбор и проверка суммы
 * пополнения, список типов операций для фильтра журнала.
 */
import type { WalletEntryKind, WalletKindOption } from '@/types/wallet';

const RUB_FORMAT = new Intl.NumberFormat('ru-RU', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** "1234.5" -> "1 234,50 ₽"; пробелы обычные, чтобы текст одинаково читался везде. */
export function formatRub(value: string | number): string {
  const num = typeof value === 'number' ? value : Number(value);
  const safe = Number.isFinite(num) ? num : 0;
  return `${RUB_FORMAT.format(safe).replace(/\s/g, ' ')} ₽`;
}

/** KAN-1003: целые рубли без знака валюты: "1000000" -> "1 000 000" (границы пополнения на /prices). */
export function formatWholeRub(value: string | number): string {
  const num = typeof value === 'number' ? value : Number(value);
  const safe = Number.isFinite(num) ? Math.round(num) : 0;
  return safe.toLocaleString('ru-RU').replace(/\s/g, ' ');
}

/** Сумма со знаком: "+1 000,00 ₽" / "-250,00 ₽". */
export function formatSignedRub(value: string): string {
  const num = Number(value);
  if (!Number.isFinite(num) || num === 0) return formatRub(0);
  return num > 0 ? `+${formatRub(num)}` : `-${formatRub(-num)}`;
}

export function isPositiveAmount(value: string): boolean {
  return Number(value) > 0;
}

/** Быстрые суммы в диалоге пополнения. */
export const QUICK_TOPUP_AMOUNTS = [1000, 5000, 10000] as const;

/** Ввод человека ("1 000,5") -> число или null. */
export function parseAmountInput(raw: string): number | null {
  const cleaned = raw.replace(/\s/g, '').replace(',', '.');
  if (cleaned === '' || !/^\d+(\.\d{1,2})?$/.test(cleaned)) return null;
  return Number(cleaned);
}

/** Число из parseAmountInput -> строка для API: 1000 -> "1000.00". */
export function toAmountString(amount: number): string {
  return amount.toFixed(2);
}

/** Текст ошибки суммы или null, если сумма допустима. */
export function validateTopupAmount(raw: string, min: number, max: number): string | null {
  const amount = parseAmountInput(raw);
  if (amount === null) return 'Введите сумму в рублях, например 1000';
  if (amount < min) return `Минимальная сумма пополнения ${formatRub(min)}`;
  if (amount > max) return `Максимальная сумма пополнения ${formatRub(max)}`;
  return null;
}

/** Запасные подписи, пока кошелёк грузится; основной список приходит в wallet.kinds. */
export const FALLBACK_WALLET_KINDS: ReadonlyArray<WalletKindOption> = [
  { value: 'topup', label: 'Пополнение' },
  { value: 'manual', label: 'Ручная правка' },
  { value: 'account', label: 'TG-аккаунт' },
  { value: 'proxy', label: 'Прокси' },
  { value: 'database', label: 'База контактов' },
  { value: 'llm', label: 'Токены OpenRouter' },
  { value: 'refund', label: 'Возврат' },
];

const ALL_KINDS_OPTION = { value: '', label: 'Все типы' } as const;

/** Опции фильтра по типу: "Все типы" + типы из ответа кошелька (или запасные). */
export function walletKindFilterOptions(
  kinds: ReadonlyArray<WalletKindOption> | undefined
): Array<{ value: string; label: string }> {
  const source = kinds && kinds.length > 0 ? kinds : FALLBACK_WALLET_KINDS;
  return [ALL_KINDS_OPTION, ...source.map((k) => ({ value: k.value, label: k.label }))];
}

export function isWalletEntryKind(value: string): value is WalletEntryKind {
  return FALLBACK_WALLET_KINDS.some((o) => o.value === value);
}

/** Дата операции: "01.11.2026, 14:05". */
export function formatEntryDate(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** KAN-1000: название раздела денег кабинета (страница, меню, ссылки). */
export const BILLING_TITLE = 'Баланс и расход';
